import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Brand } from "@/components/Layout";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

const ACTIVITY = [
  { v: "sedentary", l: "Sedentary (little exercise)" },
  { v: "light", l: "Lightly active" },
  { v: "moderate", l: "Moderately active" },
  { v: "active", l: "Very active" },
  { v: "athlete", l: "Athlete" },
];

export default function Onboarding() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [age, setAge] = useState("");
  const [gender, setGender] = useState("male");
  const [weight, setWeight] = useState("");
  const [weightUnit, setWeightUnit] = useState("kg");
  const [height, setHeight] = useState("");
  const [activity, setActivity] = useState("moderate");
  const [goal, setGoal] = useState("");
  const [loading, setLoading] = useState(false);

  if (user === null) return null;
  if (user === false) return <Navigate to="/login" replace />;
  if (user.profile) return <Navigate to="/" replace />;

  const recommendedGoal = gender === "female" ? 25 : 36;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const weight_kg = weightUnit === "lb" ? parseFloat(weight) * 0.453592 : parseFloat(weight);
    try {
      const { data } = await api.put("/profile", {
        age: parseInt(age, 10),
        gender,
        weight_kg,
        height_cm: parseFloat(height),
        activity_level: activity,
        daily_sugar_goal: goal ? parseFloat(goal) : recommendedGoal,
      });
      setUser(data);
      toast.success("Profile saved!");
      navigate("/");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-5 py-10">
      <div className="w-full max-w-lg fade-up">
        <div className="flex justify-center mb-6"><Brand size="lg" /></div>
        <div className="bg-card border border-border rounded-2xl p-8 shadow-sm">
          <h1 className="font-heading text-2xl font-black mb-1">A few details</h1>
          <p className="text-muted-foreground text-sm mb-6">We use these to personalize how much exercise burns off your sugar.</p>
          <form onSubmit={submit} className="space-y-5">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="age">Age</Label>
                <Input id="age" type="number" required min={1} max={120} value={age} onChange={(e) => setAge(e.target.value)} className="mt-1.5 rounded-xl" data-testid="onboarding-age-input" />
              </div>
              <div>
                <Label>Gender</Label>
                <Select value={gender} onValueChange={setGender}>
                  <SelectTrigger className="mt-1.5 rounded-xl" data-testid="onboarding-gender-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="male">Male</SelectItem>
                    <SelectItem value="female">Female</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="weight">Weight</Label>
                <div className="flex gap-2 mt-1.5">
                  <Input id="weight" type="number" required min={1} step="0.1" value={weight} onChange={(e) => setWeight(e.target.value)} className="rounded-xl" data-testid="onboarding-weight-input" />
                  <Select value={weightUnit} onValueChange={setWeightUnit}>
                    <SelectTrigger className="w-20 rounded-xl" data-testid="onboarding-weight-unit"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="kg">kg</SelectItem>
                      <SelectItem value="lb">lb</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label htmlFor="height">Height (cm)</Label>
                <Input id="height" type="number" required min={1} step="0.1" value={height} onChange={(e) => setHeight(e.target.value)} className="mt-1.5 rounded-xl" data-testid="onboarding-height-input" />
              </div>
            </div>

            <div>
              <Label>Activity level</Label>
              <Select value={activity} onValueChange={setActivity}>
                <SelectTrigger className="mt-1.5 rounded-xl" data-testid="onboarding-activity-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ACTIVITY.map((a) => <SelectItem key={a.v} value={a.v}>{a.l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="goal">Daily sugar goal (g)</Label>
              <Input id="goal" type="number" min={1} value={goal} onChange={(e) => setGoal(e.target.value)} placeholder={`Recommended: ${recommendedGoal}g`} className="mt-1.5 rounded-xl" data-testid="onboarding-goal-input" />
              <p className="text-xs text-muted-foreground mt-1">Leave blank to use the AHA recommendation of {recommendedGoal}g.</p>
            </div>

            <Button type="submit" disabled={loading} className="w-full rounded-full h-11 font-bold" data-testid="onboarding-submit-btn">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Start tracking"}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
